import { Country, Customer } from '~/lib/api/smartsend/types';
import { SmartSendProvider } from '~/lib/consts/providers';
import { AddressLookup } from './op/types';
import { Address } from '~/lib/api/me/types';
import { GetSignupVatStatusOptions } from '../smartsend/quotes/utils/GetVatStatuses';
import { CustomerType } from '../smartsend/quotes/utils/CustomerType';
import { useCallback } from 'react';
import useCountries from '~/hooks/use-countries';

export const MapAddressToCustomer = (address: Address): Customer => {
  const customer: Customer = {
    Name: address.name,
    CountryIsoCode: address.country.iso3Code,
    CountrySubdivision: address.country.subdivision,
    AddressLine1: address.property,
    AddressLine2: address.street,
    Town: address.town,
    Postcode: address.postcode,
    Email: address.email,
    Phone: address.phone,
    County: address.county,
    Locality: address.locality,
    Organisation: address.organisation,
    Provider: SmartSendProvider,
    Meta: {}
  };

  if (address.meta) {
    for (const k in Object.keys(address.meta)) {
      customer.Meta[k] = {
        Type: 'string',
        Value: address.meta[k]
      };
    }
  }

  return customer;
};

export const MapAddressLookupToCustomer = (
  existing: Customer,
  address: AddressLookup,
  customerType: CustomerType,
  accountName: string
): Customer => {
  const output = {
    ...existing,
    PropertyKey: `${address.property}|${address.street}|${address.postcode}|${address.countryIso}`,
    CountryIsoCode: existing.CountryIsoCode || address.countryIso,
    Postcode: address.postcode,
    Provider: SmartSendProvider,
    AccountName: accountName,
    AddressLine1: address.property && address.property.length > 0 ? address.property : address.organisation,
    AddressLine2: address.street,
    Town: address.town,
    County: address.county,
    Locality: address.locality,
    Organisation: address.organisation || existing.Organisation,
    VatStatus: GetSignupVatStatusOptions(customerType, !!address.organisation)[0],
    VatNumber: existing.VatNumber || ''
  } as Customer;

  if (address.deliveryPointSuffix) {
    output.Meta = {
      ...output.Meta,
      deliveryPointSuffix: {
        Value: address.deliveryPointSuffix,
        Type: 'string'
      }
    };
  }

  return output;
};

export const MapAddressLookupToMeAddress = (existing: Address, address: AddressLookup): Address => {
  const n = {
    ...existing,
    property: address.property && address.property.length > 0 ? address.property : address.organisation,
    street: address.street,
    town: address.town,
    county: address.county,
    postcode: address.postcode,
    locality: address.locality,
    organisation: address.organisation || existing.organisation
  };

  if (address.deliveryPointSuffix) {
    n.meta = {
      ...n.meta,
      deliveryPointSuffix: address.deliveryPointSuffix
    };
  }

  return n;
};

const getCountry = (iso3Code: string, subdivision: string, countries: Country[]) =>
  countries.find(
    (c) => c.CountryIsoCode == iso3Code && c.CountrySubdivision == (subdivision === 'null' ? null : subdivision)
  );

const getSwitchForCountry = (
  iso3Code: string,
  subdivision: string,
  county: string,
  postcode: string,
  countries: Country[]
) => {
  // Current Selection
  const country = getCountry(iso3Code, subdivision, countries);

  // Should we swicth based on postcode
  const possibleSwitch = country?.Switches?.find((s) => new RegExp(s.Pattern).test(postcode?.toUpperCase()));
  if (possibleSwitch) {
    const switchCountry = getCountry(possibleSwitch.ToIsoCode, possibleSwitch.ToSubdivision, countries);
    if (switchCountry) {
      if (switchCountry.States?.length > 0 && !switchCountry.States.find((s) => s.Code === county)) {
        return {
          county: switchCountry.States[0].Code,
          countryIsoCode: switchCountry.CountryIsoCode,
          countrySubdivision: switchCountry.CountrySubdivision
        };
      } else {
        return {
          countryIsoCode: switchCountry.CountryIsoCode,
          countrySubdivision: switchCountry.CountrySubdivision
        };
      }
    }
  }

  return null;
};

const useMapAddressLookup = () => {
  const { getLocalisedCountries } = useCountries();

  const mapAddressLookupToMeAddress = useCallback(
    async (existing: Address, address: AddressLookup): Promise<Address> => {
      const updated = MapAddressLookupToMeAddress(existing, address);

      // Country Switching Logic
      try {
        const countries = await getLocalisedCountries();
        const countySwitch = getSwitchForCountry(
          updated.country.iso3Code,
          updated.country.subdivision,
          updated.county,
          updated.postcode,
          countries
        );

        if (countySwitch) {
          updated.country.iso3Code = countySwitch.countryIsoCode;
          updated.country.subdivision = countySwitch.countrySubdivision;
          if (countySwitch.county) {
            updated.county = countySwitch.county;
          }
        }
      } catch (e) {
        console.error('Error during country switch lookup', e);
      }

      return updated;
    },
    [getLocalisedCountries]
  );

  const mapAddressLookupToCustomer = useCallback(
    async (
      existing: Customer,
      address: AddressLookup,
      customerType: CustomerType,
      accountName: string
    ): Promise<Customer> => {
      const updated = MapAddressLookupToCustomer(existing, address, customerType, accountName);

      // Country Switching Logic
      try {
        const countries = await getLocalisedCountries();
        const countySwitch = getSwitchForCountry(
          updated.CountryIsoCode,
          updated.CountrySubdivision,
          updated.County,
          updated.Postcode,
          countries
        );

        if (countySwitch) {
          updated.CountryIsoCode = countySwitch.countryIsoCode;
          updated.CountrySubdivision = countySwitch.countrySubdivision;
          if (countySwitch.county) {
            updated.County = countySwitch.county;
          }
        }
      } catch (e) {
        console.error('Error during country switch lookup', e);
      }

      return updated;
    },
    [getLocalisedCountries]
  );

  return { mapAddressLookupToMeAddress, mapAddressLookupToCustomer } as const;
};

export default useMapAddressLookup;
